import React, { useEffect, useState, Children } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, MailIcon, GithubIcon, LinkedinIcon, GlobeIcon } from 'lucide-react';
import cvData from '../../data/cv';
import { Stat } from './Stat';
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15
    }
  }
};
const rise = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function Hero() {
  const {
    profile,
    stats,
    meta
  } = cvData;
  const {
    scrollY
  } = useScroll();
  const reduce = useReducedMotion();
  const y = useTransform(scrollY, [0, 600], [0, 140]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  // Scroll cue fades out as soon as the user starts scrolling.
  const cueOpacity = useTransform(scrollY, [0, 80], [1, 0]);
  const [first, ...rest] = profile.name.split(' ');
  const lastFull = rest.join(' ');
  // Abbreviated surname, e.g. "Assali" -> "A."
  const last = lastFull ? `${lastFull.charAt(0)}.` : '';
  const [countStart, setCountStart] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setCountStart(true), 700);
    return () => window.clearTimeout(t);
  }, []);
  const links = [profile.email && {
    href: `mailto:${profile.email}`,
    Icon: MailIcon,
    label: profile.email
  }, profile.website && {
    href: `https://${profile.website}`,
    Icon: GlobeIcon,
    label: profile.website
  }, profile.linkedin && {
    href: `https://${profile.linkedin}`,
    Icon: LinkedinIcon,
    label: profile.linkedin.replace('linkedin.com/in/', '@')
  }, profile.github && {
    href: `https://${profile.github}`,
    Icon: GithubIcon,
    label: profile.github.replace('github.com/', '@')
  }].filter(Boolean) as {
    href: string;
    Icon: typeof MailIcon;
    label: string;
  }[];
  return <section id="intro" className="relative flex min-h-[88vh] flex-col justify-center overflow-hidden px-6 pb-16 pt-28 md:px-12" data-id="element-257" data-mp="25idubirra44q">
      {/* slow-drifting accent aurora behind the name */}
      <motion.div aria-hidden="true" className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full opacity-[0.16] blur-[120px]" style={{
      background: 'var(--accent)'
    }} animate={reduce ? undefined : {
      x: [0, -40, 20, 0],
      y: [0, 30, -20, 0],
      scale: [1, 1.12, 0.96, 1]
    }} transition={{
      duration: 22,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-258" data-mp="1vriybmopjq63" />
      <motion.div aria-hidden="true" className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full opacity-[0.10] blur-[130px]" style={{
      background: '#7c4a2d'
    }} animate={reduce ? undefined : {
      x: [0, 40, -10, 0],
      y: [0, -30, 20, 0]
    }} transition={{
      duration: 26,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-259" data-mp="378963a8s4q1t" />

      <motion.div style={{
      y,
      opacity
    }} className="relative mx-auto w-full max-w-6xl" data-id="element-260" data-mp="15fp8xcllpgun">
        <motion.div variants={container} initial="hidden" animate="show" data-id="element-261" data-mp="1ealtf1arufxt">
          <motion.p variants={rise} className="font-mono text-[12px] uppercase tracking-[0.4em] text-[var(--accent)]" data-id="element-262" data-mp="1t7cq32t29djq">
            {meta.kicker}
          </motion.p>

          <motion.h1 variants={rise} className="mt-5 flex items-baseline gap-3 font-display text-[clamp(52px,10vw,120px)] font-light leading-[0.9] tracking-tight text-white sm:gap-5" data-id="element-263" data-mp="3uyps9k3n97c3">
            {first}
            {last && <span className="italic text-stone-400" data-id="element-264" data-mp="2h1wpughy6c9t">{last}</span>}
          </motion.h1>

          <motion.p variants={rise} className="mt-7 max-w-2xl text-[16px] leading-relaxed text-stone-300" data-id="element-265" data-mp="2m0q3qa15c5ai">
            {cvData.summary[0]}
          </motion.p>

          {/* stat row */}
          <motion.dl variants={rise} className="mt-8 flex flex-wrap gap-x-10 gap-y-4" data-id="element-266" data-mp="2v1o8iujesgu5">
            {stats.map(s => <Stat key={s.label} value={s.value} label={s.label} start={countStart} data-id="element-267" data-mp="23arrdhqbfq12" />)}
          </motion.dl>

          {/* links */}
          <motion.div variants={rise} className="mt-10 flex flex-wrap gap-2.5" data-id="element-268" data-mp="2c4emd5mp8p97">
            {links.map(({
            href,
            Icon,
            label
          }) => <a key={label} href={href} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 font-mono text-[12px] text-stone-300 transition-colors hover:border-[var(--accent)] hover:text-white" data-id="element-269" data-mp="1fjw6z5o6vx3u">
                <Icon className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" data-id="element-270" data-mp="131s85rn7nblp" />
                {label}
              </a>)}
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div aria-hidden="true" style={{
      opacity: cueOpacity
    }} className="absolute bottom-6 right-6 flex items-center gap-2 text-stone-500 md:right-12" data-id="element-271" data-mp="oegthgvqfbhd">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em]" data-id="element-272" data-mp="fe2528nh1g9n">
          Scroll
        </span>
        <motion.span animate={reduce ? undefined : {
        y: [0, 5, 0]
      }} transition={{
        repeat: Infinity,
        duration: 1.6,
        ease: 'easeInOut'
      }} data-id="element-273" data-mp="aepat2u9t6x1">
          <ArrowDownIcon className="h-4 w-4" data-id="element-274" data-mp="r3esh0corwi" />
        </motion.span>
      </motion.div>
    </section>;
}